import { ClientSettings, ClientSettingsGroup, ClientSettingsRow } from '@codezo/client-detail';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { InvoiceRecipientsRow } from './InvoiceRecipientsRow';
import { PAYMENT_MODE_OPTIONS, useClientSettings } from './useClientSettings';

/**
 * The v2 settings section: three groups of label / explanation / one control.
 *
 * All behaviour comes from `useClientSettings`; this file is arrangement and
 * controls. Each CARD saves on its own, from one bar at its bottom edge — the
 * card is the unit an admin thinks in ("set up how this client pays"), and the
 * hook fires only the mutations whose fields actually changed. The arrangement
 * this replaces put a Save inline in the card's header next to the caveat text,
 * and two more beside individual rows, so committing the Payment card meant
 * finding and pressing three buttons in three places.
 */

/**
 * A number with its unit welded on, and a Reset that only appears when the
 * value differs from the system default.
 *
 * Empty means "use the system default", which is why Reset clears rather than
 * writing the default number in: storing a copy of the default would silently
 * stop tracking it if the system value ever changed.
 */
function UnitInput({
	value,
	unit,
	onChange,
	placeholder,
	resetLabel = 'Use system default',
}: {
	value: string;
	unit: string;
	onChange: (value: string) => void;
	/** The system default, shown greyed when the field is empty. */
	placeholder?: string | number | null;
	resetLabel?: string;
}) {
	return (
		/* The reset sits INSIDE the field, on the same fixed width. Hung outside
		   it as a word, it read as a loose control belonging to the row rather
		   than to this number, and it changed the row's width as it came and
		   went — every field in the card shifted when one of them was edited. */
		<div className="flex h-8 w-[10.5rem] items-center overflow-hidden rounded-[var(--r-2)] border border-line-2 bg-surface-fill focus-within:border-line-3">
			<span className="flex h-full w-7 shrink-0 items-center justify-center">
				{value !== '' && (
					<button
						type="button"
						onClick={() => onChange('')}
						title={resetLabel}
						aria-label={resetLabel}
						className="flex h-5 w-5 items-center justify-center rounded-[var(--r-1)] bg-transparent p-0 text-text-4 transition-colors hover:bg-surface hover:text-text-1"
					>
						<RotateCcw size={11} />
					</button>
				)}
			</span>
			<input
				value={value}
				inputMode="decimal"
				placeholder={placeholder != null ? String(placeholder) : undefined}
				onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
				className="h-full min-w-0 flex-1 border-none bg-transparent px-2 text-right font-mono text-xs text-text-1 outline-none placeholder:text-text-4"
			/>
			<span className="flex h-full items-center border-l border-line-1 px-2.5 font-mono text-2xs text-text-4">
				{unit}
			</span>
		</div>
	);
}

/**
 * A card's save bar: what changed, a way back, and one commit.
 *
 * It renders only once the card is dirty. Nothing on this page auto-saves, so
 * without the bar there is no sign that a toggle flipped or a number typed is
 * still only on screen — and Discard is what makes that safe to find out.
 */
function SaveBar({
	dirty,
	loading,
	onSave,
	onDiscard,
}: {
	dirty: boolean;
	loading: boolean;
	onSave: () => void;
	onDiscard: () => void;
}) {
	if (!dirty) return null;
	return (
		<div className="flex flex-wrap items-center justify-end gap-2.5">
			<span className="mr-auto text-2xs text-text-3">Unsaved changes</span>
			<Button size="sm" variant="ghost" onClick={onDiscard} disabled={loading}>
				Discard
			</Button>
			<Button size="sm" onClick={onSave} disabled={loading}>
				{loading ? 'Saving…' : 'Save changes'}
			</Button>
		</div>
	);
}

export function ClientSettingsSection({ organizationId }: { organizationId: string }) {
	const s = useClientSettings(organizationId);

	if (s.loading) {
		return (
			<ClientSettings>
				<div className="h-28 animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface" />
				<div className="h-44 animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface" />
			</ClientSettings>
		);
	}

	const modeSelect = (value: string, onChange: (v: string) => void) => (
		<Select
			items={PAYMENT_MODE_OPTIONS}
			value={value}
			onValueChange={(v) => {
				if (v) onChange(v);
			}}
		>
			<SelectTrigger className="w-[13.75rem]">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{PAYMENT_MODE_OPTIONS.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);

	return (
		<ClientSettings>
			<ClientSettingsGroup
				title="Quotes"
				note="defaults for every new quote"
				footer={
					<SaveBar
						dirty={s.approvalDirty}
						loading={s.updatingApproval}
						onSave={s.saveApproval}
						onDiscard={s.discardApproval}
					/>
				}
			>
				<ClientSettingsRow
					label="Auto-approve quotes"
					description="New quotes for this client skip internal approval."
				>
					<Switch checked={s.defaultApproval} onCheckedChange={s.setDefaultApproval} />
				</ClientSettingsRow>
				<ClientSettingsRow
					label="Default margin"
					description="Applied to supplier cost on every new quote line."
					last
				>
					<UnitInput value={s.defaultMargin} unit="%" onChange={s.setDefaultMargin} />
				</ClientSettingsRow>
			</ClientSettingsGroup>

			<ClientSettingsGroup
				title="Payment"
				note="snapshotted to each order when accepted"
				footer={
					<SaveBar
						dirty={s.paymentDirty}
						loading={s.savingPayment}
						onSave={s.savePayment}
						onDiscard={s.discardPayment}
					/>
				}
			>
				<ClientSettingsRow
					label="Product payment"
					description="How the client pays for production orders."
				>
					{modeSelect(s.productPaymentMode, (v) =>
						s.setProductPaymentMode(v as typeof s.productPaymentMode)
					)}
				</ClientSettingsRow>
				<ClientSettingsRow
					label="Sample payment"
					description="How the client pays for samples."
				>
					{modeSelect(s.samplePaymentMode, (v) =>
						s.setSamplePaymentMode(v as typeof s.samplePaymentMode)
					)}
				</ClientSettingsRow>
				<ClientSettingsRow
					label="Stripe checkout limit"
					description="Max payable through Stripe in one checkout. Orders can override. Empty uses the system default."
				>
					<UnitInput
						value={s.thresholdInput}
						unit="EUR"
						onChange={s.setThresholdInput}
						placeholder={s.defaultStripeLimit}
					/>
				</ClientSettingsRow>
				<ClientSettingsRow
					label="Invoice minimum"
					description="Below this amount the client can only pay via Stripe. Empty uses the system default."
					last
				>
					<UnitInput
						value={s.invoiceMinInput}
						unit="DKK"
						onChange={s.setInvoiceMinInput}
						placeholder={s.defaultInvoiceMin}
					/>
				</ClientSettingsRow>
			</ClientSettingsGroup>

			<ClientSettingsGroup
				title="Invoicing"
				note="terms sync to the e-conomic customer record"
				footer={
					<SaveBar
						dirty={s.paymentTermsDirty}
						loading={s.updatingPaymentTerms}
						onSave={s.savePaymentTerms}
						onDiscard={s.discardPaymentTerms}
					/>
				}
			>
				<ClientSettingsRow
					label="Payment terms"
					description={
						s.paymentTermsUnavailable
							? 'e-conomic is unreachable, so the term list cannot be loaded. The saved term is unchanged.'
							: 'Due date on every invoice.'
					}
					tag={
						<span className="inline-flex items-center gap-1 font-mono text-2xs font-normal text-text-4">
							<RefreshCw size={10} />
							e-conomic
						</span>
					}
				>
					<Select
						items={s.paymentTermsOptions}
						value={s.paymentTermsNumber}
						onValueChange={(v) => {
							if (v) s.setPaymentTermsNumber(v);
						}}
						disabled={s.paymentTermsUnavailable}
					>
						<SelectTrigger className="w-[13.75rem]">
							<SelectValue placeholder="Select terms" />
						</SelectTrigger>
						<SelectContent>
							{s.paymentTermsOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</ClientSettingsRow>

				<InvoiceRecipientsRow organizationId={organizationId} />
			</ClientSettingsGroup>
		</ClientSettings>
	);
}
